// What went wrong when utilization could not be loaded.
//
// The section used to render one string for every failure, which is the shape
// docs/architecture/station-online-status.md warns about (#1579/#1581): a
// failure that looks like an empty state never gets reported accurately. It
// cost a real round trip — a stale ECS image, an unset
// REACT_APP_API_NEW_BACKEND_URL, a service that is down and a rejected range
// all printed "Auslastung konnte nicht geladen werden", so diagnosing it meant
// opening devtools to read the status code off the request.
//
// `newBackendAxiosBaseQuery` already carries what is needed
// (utils/axiosBaseQuery.ts): `{ status, data }` for an axios error, with
// `status` undefined when no response came back at all. This turns that into
// the one distinction an operator can act on — "the service did not answer" vs
// "it answered, and said no" — while keeping the code visible so the report
// names it.

export type UtilizationErrorKind =
    /** No response reached us: wrong base URL, service down, network. */
    | 'unreachable'
    /** The route answered 404 — typically a deployment without it yet. */
    | 'not_found'
    /** The request was rejected, e.g. by the range guard. */
    | 'bad_request'
    | 'unauthorized'
    | 'server'
    | 'unknown';

export interface UtilizationError {
    kind: UtilizationErrorKind;
    /** HTTP status, where one came back. */
    status?: number;
}

const hasStatusKey = (error: unknown): error is { status?: unknown } =>
    typeof error === 'object' && error !== null && 'status' in error;

/**
 * Classify an RTK Query error from the new-backend base query.
 *
 * Deliberately tolerant of shape: the base query returns `{ status, data }` for
 * an axios error and the raw error otherwise, and the slice's error type is
 * `unknown`, so this narrows rather than casting.
 */
export const utilizationErrorKind = (error: unknown): UtilizationError => {
    if (!hasStatusKey(error)) return { kind: 'unknown' };

    const { status } = error;
    // An axios error with no response — the request never reached a service
    // that answers. This is the one an operator can act on without us.
    if (status === undefined || status === null) return { kind: 'unreachable' };
    if (typeof status !== 'number') return { kind: 'unknown' };

    if (status === 404) return { kind: 'not_found', status };
    if (status === 400) return { kind: 'bad_request', status };
    if (status === 401 || status === 403) return { kind: 'unauthorized', status };
    if (status >= 500) return { kind: 'server', status };
    return { kind: 'unknown', status };
};
